import type { IconType } from "react-icons";
import {
    FaBuilding,
    FaShieldAlt,
    FaLayerGroup,
    FaEye,
    FaRobot,
    FaFileInvoiceDollar,
    FaBoxes,
    FaBalanceScale,
} from "react-icons/fa";

export const easyonHighlights = [
    { title: "Multi Company Support", icon: FaBuilding },
    { title: "Secure Access", icon: FaShieldAlt },
    { title: "Centralized Operations", icon: FaLayerGroup },
    { title: "Real-Time Visibility", icon: FaEye },
];

export const easyonPlatforms = [
    { name: "Android", iconKey: "android" as const },
    { name: "iOS", iconKey: "ios" as const },
    { name: "Chrome Extension", iconKey: "chrome" as const },
    { name: "Desktop", iconKey: "desktop" as const },
    { name: "Web", iconKey: "web" as const },
];

export const easyonDatabases = [
    { name: "PostgreSQL", iconKey: "postgresql" as const, description: "Enterprise-grade relational storage for high-volume operations." },
    { name: "Google Sheets", iconKey: "sheets" as const, description: "Flexible sync for teams that live in spreadsheets." },
    { name: "SQLite", iconKey: "sqlite" as const, description: "Lightweight local database for offline-first workflows." },
];

export const easyonModules: {
    title: string;
    description: string;
    icon: IconType;
    span: "wide" | "tall" | "normal";
}[] = [
    {
        title: "AI Assistant",
        description: "Draft quotations, invoices, and purchase orders using voice or chat.",
        icon: FaRobot,
        span: "wide",
    },
    {
        title: "Quotations & Billing",
        description: "Generate quotations, invoices, sales orders, proformas, and billing documents.",
        icon: FaFileInvoiceDollar,
        span: "normal",
    },
    {
        title: "Inventory & Purchase",
        description: "Track stock levels, manage vendors, monitor inventory, and streamline purchase workflows.",
        icon: FaBoxes,
        span: "tall",
    },
    {
        title: "GST & Compliance",
        description: "Generate e-Way bills, manage returns, and maintain tax-ready records.",
        icon: FaBalanceScale,
        span: "normal",
    },
];

export const easyonWorkflowSteps = [
    "Customer Inquiry",
    "Quotation",
    "Order",
    "Invoice",
    "Payment",
    "GST Compliance",
];

export const easyonDashboardWidgets = [
    { label: "Pending Quotations", value: "24", change: "+6 today", iconKey: "quote" as const },
    { label: "Revenue", value: "₹12.8L", change: "+18% MoM", iconKey: "revenue" as const },
];

export const easyonBusinessImpact = [
    { value: 70, suffix: "%", label: "Faster Billing" },
    { value: 60, suffix: "%", label: "Reduced Manual Work" },
    { value: 50, suffix: "%", label: "Faster Quotation Creation" },
    { value: 99, suffix: "%", label: "Data Accuracy" },
];

export const easyonIndustries = [
    "Manufacturing",
    "Retail",
    "Trading",
    "Education",
    "Healthcare",
    "Logistics",
];

export const easyonComplianceTimeline = [
    { title: "Invoice Prepared", note: "Auto tax mapping and HSN validation" },
    { title: "e-Invoice Generated", note: "Signed and synced with records" },
    { title: "e-Way Bill Created", note: "Transport-ready compliance document" },
    { title: "GST Report Generated", note: "Auto-generated GST summaries, tax calculations, and compliance reports" },
];

export const easyonFaqItems = [
    {
        question: "What is EasyOn?",
        answer: "EasyOn is a business operations automation platform that unifies quotations, billing, inventory, purchases, vendors, and GST compliance in one intelligent workspace built for growing teams.",
    },
    {
        question: "Can EasyOn manage multiple companies?",
        answer: "Yes. EasyOn supports multi-company operations with secure role-based access, so you can manage separate entities, books, and teams from a single login.",
    },
    {
        question: "Does it support GST compliance?",
        answer: "Absolutely. EasyOn helps you generate e-Way bills , e-Invoices and you can download gst reports.",
    },
    {
        question: "Can I manage inventory and purchases?",
        answer: "Yes. Track stock levels in real time, manage vendor relationships, create purchase orders, and streamline procurement workflows end to end.",
    },
    {
        question: "Which platforms are supported?",
        answer: "EasyOn runs on Android, iOS, Web, Desktop, and offers a Chrome Extension — so your team can work from anywhere.",
    },
    {
        question: "Does EasyOn include AI assistance?",
        answer: "Yes. The built-in AI assistant lets you draft quotations, invoices, and purchase orders through natural voice or chat commands.",
    },
    {
        question: "What databases are supported?",
        answer: "EasyOn supports PostgreSQL for enterprise deployments, Google Sheets for flexible sync, and SQLite for lightweight offline-first setups.",
    },
    {
        question: "Is EasyOn suitable for SMEs?",
        answer: "Yes. EasyOn is purpose-built for small and medium businesses that need professional operations tooling without enterprise complexity or cost.",
    },
];
